import { Alert, Button, ColumnLayout, FormField, Input, Modal, Table} from "@amzn/awsui-components-react";
import React from "react";
import { addSPOCAdjustment, getSPOCAdjustments } from "../../utils/mapReleaseDashboard/mapReleaseApiCall";

interface SpocEntry {
    station: string;
    trainingMethod: string;
    currentSPOC: string;
    newSPOC: string;
}

const SpocAdjustmentModal = ({mapPack}: {mapPack: string}) => {
    const [isModalVisible, setModalVisible] = React.useState(false);
    const [stationField, setStationField] = React.useState("");
    const [traningMethodField, setTraningMethodField] = React.useState("Override");
    const [currSPOCField, setCurrSPOCField] = React.useState("");
    const [newSPOCField, setNewSPOCField] = React.useState("");
    const [addMsg, setAddMsg] = React.useState("");
    const [spocEntries, setSpocEntries] = React.useState<SpocEntry[]>([]);
    const [loadingSPOCs, setLoadingSPOCs] = React.useState(false);
    const handleSpocAdjustment = () => {
        setModalVisible(true);
        loadExistingSPOCs(); // Load existing SPOCs when modal opens
    }

    // Load existing SPOCs from backend using the dedicated API function
    const loadExistingSPOCs = async () => {
        setLoadingSPOCs(true);
        try {
            const mapPackId = mapPack.split("-").slice(2, 3).join("-");
            const response = await getSPOCAdjustments(mapPackId);
            
            if (Array.isArray(response)) {
                // Filter out invalid entries
                const validEntries = response.filter((entry: SpocEntry) =>
                    entry.station && entry.trainingMethod && entry.currentSPOC && entry.newSPOC
                );
                setSpocEntries(validEntries);
            } else if (response && typeof response === 'string' && response.trim() !== "") {
                try {
                    const parsedResponse = JSON.parse(response);
                    if (Array.isArray(parsedResponse)) {
                        const validEntries = parsedResponse.filter((entry: SpocEntry) =>
                            entry.station && entry.trainingMethod && entry.currentSPOC && entry.newSPOC
                        );
                        setSpocEntries(validEntries);
                    } else {
                        setSpocEntries([]);
                    }
                } catch (parseError) {
                    console.error("Failed to parse JSON response:", parseError);
                    setSpocEntries([]);
                }
            } else {
                setSpocEntries([]); // Empty array if no data
            }
        } catch (error) {
            console.error("Error loading SPOC adjustments:", error);
            setSpocEntries([]);
        } finally {
            setLoadingSPOCs(false);
        }
    }


    const refreshFields = () => {
        addSPOCAdjustment(mapPack.split("-").slice(2, 3).join("-"), stationField, traningMethodField, currSPOCField, newSPOCField);
        
        // Add to local state immediately for display
        const newEntry: SpocEntry = {
            station: stationField,
            trainingMethod: traningMethodField,
            currentSPOC: currSPOCField,
            newSPOC: newSPOCField
        };
        setSpocEntries(prev => [...prev, newEntry]);
        setAddMsg(`Added adjustment [Station: ${stationField}, Method: ${traningMethodField}, Current: ${currSPOCField}, New: ${newSPOCField}]`);
        setStationField("");
        setTraningMethodField("Override");
        setCurrSPOCField("");
        setNewSPOCField("");
    }

    const resetModal = () => {
        setStationField("");
        setTraningMethodField("Override");
        setCurrSPOCField("");
        setNewSPOCField("");
        setAddMsg("");
        setSpocEntries([]);
        setModalVisible(false);
    }

    // Table column definitions for SPOC display on UI
    const spocTableColumns = [
        {
            id: "station",
            header: "Station",
            cell: (item: SpocEntry) => item.station
        },
        {
            id: "trainingMethod",
            header: "Training Method",
            cell: (item: SpocEntry) => item.trainingMethod
        },
        {
            id: "currentSPOC",
            header: "Current SPOC",
            cell: (item: SpocEntry) => item.currentSPOC
        },
        {
            id: "newSPOC",
            header: "New SPOC",
            cell: (item: SpocEntry) => item.newSPOC
        }
    ];

    return (
    <>
        <Button iconName="edit" onClick={handleSpocAdjustment} wrapText={false}>
            Input Adjustments
        </Button>
        <Modal
            visible={isModalVisible}
            onDismiss={resetModal}
            header="SPOC Adjustments"
            size="large"
            footer={
                <span className="awsui-util-f-r">
                    <Button variant="primary" onClick={refreshFields} 
                        disabled={!stationField || !traningMethodField || !currSPOCField || !newSPOCField}>
                        Add
                    </Button>
                </span>
            }
        >
            
            <ColumnLayout>
                <FormField label="Station">
                    <Input value={stationField}
                    onChange={({ detail }) => setStationField(detail.value)}
                    />
                </FormField>
                <FormField label="Training Method">
                    <Input value={traningMethodField}
                    onChange={({ detail }) => setTraningMethodField(detail.value)}
                    />
                </FormField>
                <FormField label="Current SPOC">
                    <Input value={currSPOCField}
                    onChange={({ detail }) => setCurrSPOCField(detail.value)}
                   />
                </FormField>
                <FormField label="New SPOC">
                    <Input value={newSPOCField}
                    onChange={({ detail }) => setNewSPOCField(detail.value)}
                    />
                </FormField>
            </ColumnLayout>
            <br></br>
            {addMsg && 
            <Alert type="success" 
                    dismissible={true}
                    onDismiss={() => setAddMsg("")}>
                {addMsg}
            </Alert>}

            <br></br>
            <h3>SPOC Adjustments Added:</h3>
            <Table
                columnDefinitions={spocTableColumns}
                items={spocEntries}
                loading={loadingSPOCs}
                loadingText="Loading existing SPOCs"
                empty={
                    <div style={{ textAlign: 'center' }}>
                        {loadingSPOCs ? "Loading" : "No SPOC adjustments added yet. Add some using the form above."}
                    </div>
                }
            />
                
            
        </Modal>
    </>
);
}

export default SpocAdjustmentModal;
